/**
 * Ce qui relie la mesure d'audience à l'application : la session de l'opérateur,
 * et les pages qu'il ouvre.
 */

import { useEffect } from 'react';

import { useLocation } from 'react-router-dom';

import type { Analytics } from './analytics.js';
import type { SessionStore } from '../state/session-store.js';

/**
 * Suit la session : l'opérateur est présenté quand elle s'ouvre ou change de main,
 * et oublié quand elle se ferme — par déconnexion ou par expiration.
 *
 * Rend la fonction qui cesse le suivi.
 */
export function followSession(store: SessionStore, analytics: Analytics): () => void {
  const presenter = (operatorId: string | undefined): void => {
    if (operatorId !== undefined && operatorId !== '') analytics.identify(operatorId);
  };
  presenter(store.getState().operator?.operatorId);
  return store.subscribe((etat, precedent) => {
    const avant = precedent.operator;
    const apres = etat.operator;
    if (avant !== null && apres === null) {
      // Un poste de back-office est souvent partagé : l'opérateur suivant ne
      // doit pas être compté sous l'identifiant de celui qui part.
      analytics.reset();
      return;
    }
    if (apres?.operatorId !== avant?.operatorId) presenter(apres?.operatorId);
  });
}

/** Rapporte chaque page ouverte. Le chemin est nettoyé par la mesure elle-même. */
export function AnalyticsPageViews({ analytics }: { readonly analytics: Analytics }): null {
  const { pathname } = useLocation();
  useEffect(() => {
    analytics.page(pathname);
  }, [analytics, pathname]);
  return null;
}
